import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';

const userPoolId = process.env.REACT_APP_USER_POOL_ID || 'us-east-1_dummy';
const clientId = process.env.REACT_APP_USER_POOL_CLIENT_ID || 'dummy-client-id';

const poolData = {
  UserPoolId: userPoolId,
  ClientId: clientId,
};

const userPool = new CognitoUserPool(poolData);

export interface AuthUser {
  userId: string;
  email: string;
  username: string;
  groups?: string[];
}

/**
 * Sign up a new user
 */
export const signUp = async (
  email: string,
  password: string,
  name?: string
): Promise<CognitoUser> => {
  const normalizedEmail = email.trim().toLowerCase();
  return new Promise((resolve, reject) => {
    const attributeList: CognitoUserAttribute[] = [];

    const dataEmail = {
      Name: 'email',
      Value: normalizedEmail,
    };
    attributeList.push(new CognitoUserAttribute(dataEmail));

    if (name) {
      const dataName = {
        Name: 'name',
        Value: name,
      };
      attributeList.push(new CognitoUserAttribute(dataName));
    }

    userPool.signUp(normalizedEmail, password, attributeList, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(result!.user);
    });
  });
};

/**
 * Confirm sign up with verification code
 */
export const confirmSignUp = async (
  email: string,
  code: string
): Promise<void> => {
  const normalizedEmail = email.trim().toLowerCase();
  return new Promise((resolve, reject) => {
    const userData = {
      Username: normalizedEmail,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmRegistration(code, true, (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
};

/**
 * Sign in user
 */
export const signIn = async (
  email: string,
  password: string
): Promise<CognitoUserSession> => {
  const normalizedEmail = email.trim().toLowerCase();
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: normalizedEmail,
      Password: password,
    });

    const userData = {
      Username: normalizedEmail,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Sign out user
 */
export const signOut = (): void => {
  const cognitoUser = userPool.getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
};

/**
 * Get current user session
 */
export const getCurrentSession =
  async (): Promise<CognitoUserSession | null> => {
    return new Promise((resolve, reject) => {
      const cognitoUser = userPool.getCurrentUser();

      if (!cognitoUser) {
        resolve(null);
        return;
      }

      cognitoUser.getSession(
        (err: Error | null, session: CognitoUserSession | null) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(session);
        }
      );
    });
  };

/**
 * Get access token from current session
 */
export const getAccessToken = async (): Promise<string | null> => {
  try {
    const session = await getCurrentSession();
    if (!session) {
      console.warn('AuthService - No session found');
      return null;
    }

    if (!session.isValid()) {
      console.warn('AuthService - Session is invalid');
      return null;
    }

    // Get ACCESS token (not ID token)
    const accessToken = session.getAccessToken().getJwtToken();

    // Decode and log token payload for debugging
    try {
      const payload = JSON.parse(atob(accessToken.split('.')[1]));
      console.log('AuthService - Access token info:', {
        token_use: payload.token_use, // Should be 'access'
        client_id: payload.client_id,
        username: payload.username,
        exp: new Date(payload.exp * 1000).toISOString(),
        isExpired: payload.exp * 1000 < Date.now(),
        tokenPreview: accessToken.substring(0, 30) + '...',
      });

      if (payload.token_use !== 'access') {
        console.error(
          'AuthService - WARNING: Token is not an access token!',
          payload.token_use
        );
      }

      if (payload.exp * 1000 < Date.now()) {
        console.error('AuthService - WARNING: Token is expired!');
      }
    } catch (decodeError) {
      console.error('AuthService - Error decoding token:', decodeError);
    }

    return accessToken;
  } catch (error) {
    console.error('AuthService - Error getting access token:', error);
    return null;
  }
};

/**
 * Get ID token from current session (for user info, not API auth)
 */
export const getIdToken = async (): Promise<string | null> => {
  try {
    const session = await getCurrentSession();
    if (!session) {
      console.warn('AuthService - No session found');
      return null;
    }

    if (!session.isValid()) {
      console.warn('AuthService - Session is invalid');
      return null;
    }

    // Get ID token
    const idToken = session.getIdToken().getJwtToken();

    console.log('AuthService - ID token info:', {
      tokenPreview: idToken.substring(0, 30) + '...',
    });

    return idToken;
  } catch (error) {
    console.error('AuthService - Error getting ID token:', error);
    return null;
  }
};

/**
 * Get current user info
 */
export const getCurrentUser = async (): Promise<AuthUser | null> => {
  try {
    const session = await getCurrentSession();
    if (!session) {
      return null;
    }

    const idToken = session.getIdToken();
    const payload = idToken.payload;

    return {
      userId: payload.sub,
      email: payload.email,
      username: payload['cognito:username'] || payload.email,
      groups: payload['cognito:groups'] || [],
    };
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

/**
 * Refresh session
 */
export const refreshSession = async (): Promise<CognitoUserSession> => {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      reject(new Error('No user found'));
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err) {
          reject(err);
          return;
        }

        if (!session) {
          reject(new Error('No session found'));
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(refreshToken, (err, newSession) => {
          if (err) {
            reject(err);
            return;
          }
          resolve(newSession);
        });
      }
    );
  });
};

/**
 * Forgot password - initiate reset
 */
export const forgotPassword = async (email: string): Promise<void> => {
  const normalizedEmail = email.trim().toLowerCase();
  return new Promise((resolve, reject) => {
    const userData = {
      Username: normalizedEmail,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};

/**
 * Confirm password reset with code
 */
export const confirmPassword = async (
  email: string,
  code: string,
  newPassword: string
): Promise<void> => {
  const normalizedEmail = email.trim().toLowerCase();
  return new Promise((resolve, reject) => {
    const userData = {
      Username: normalizedEmail,
      Pool: userPool,
    };

    const cognitoUser = new CognitoUser(userData);

    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
};
